import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICE_CATEGORIES } from "@/data";

export default function Services() {
  const [active, setActive] = useState(SERVICE_CATEGORIES[0].id);
  const current = SERVICE_CATEGORIES.find((c) => c.id === active);

  return (
    <section
      id="services"
      className="relative z-10 bg-[#14171A] py-20 md:py-32 border-t border-[#A8AEB2]/15"
      data-testid="services-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="font-body text-sm uppercase tracking-[0.4em] text-[#7AB648]">
              What We Do
            </span>
            <h2 className="font-display uppercase text-5xl md:text-7xl text-white leading-[0.9] mt-4">
              Services &amp; Pricing
            </h2>
          </motion.div>
          <p className="max-w-sm font-body text-[#A8AEB2] text-base md:text-lg">
            From the roof down to the ground beneath it — one trusted team for the
            whole job.
          </p>
        </div>

        {/* Category tabs */}
        <div
          className="flex flex-wrap gap-2 md:gap-3 mb-10 border-b border-[#A8AEB2]/15 pb-6"
          role="tablist"
        >
          {SERVICE_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              role="tab"
              aria-selected={active === cat.id}
              onClick={() => setActive(cat.id)}
              className={`rounded-sm px-5 py-3 font-head font-bold uppercase text-sm tracking-wide transition-colors active:scale-95 ${
                active === cat.id
                  ? "bg-[#7AB648] text-[#14171A]"
                  : "bg-[#1E2227] text-[#A8AEB2] border border-[#A8AEB2]/15 hover:border-[#7AB648] hover:text-white"
              }`}
              data-testid={`services-tab-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`services-panel-${active}`}
          >
            <p className="font-body text-lg md:text-xl text-white mb-8 max-w-2xl">
              {current.blurb}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-14 gap-y-0">
              {current.items.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="group flex items-center justify-between gap-4 border-b border-[#A8AEB2]/15 py-5 transition-colors hover:border-[#7AB648]"
                  data-testid={`service-item-${current.id}-${i}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <span className="font-head text-xs text-[#7AB648] w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-head font-medium text-lg md:text-xl text-white tracking-tight truncate group-hover:translate-x-1 transition-transform">
                      {item.name}
                    </span>
                  </div>
                  <span className="shrink-0 font-body text-sm md:text-base font-semibold text-[#7AB648] whitespace-nowrap">
                    {item.price}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <p className="font-body text-sm text-[#A8AEB2] italic">
            Prices available following a free site visit and quotation.
          </p>
          <a
            href="#bookings"
            className="group inline-flex items-center gap-2 rounded-sm bg-[#7AB648] px-7 py-4 font-head font-bold uppercase tracking-wide text-[#14171A] transition-colors hover:bg-[#6BA339] active:scale-95 self-start"
            data-testid="services-quote-button"
          >
            Get a Free Quote
            <ArrowUpRight size={20} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </div>
      </div>
    </section>
  );
}
